// src/services/api.js

// ============================================
// URL Configuration
// ============================================

const getBaseUrl = () => {
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                        window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';
  
  // ✅ Use the same path structure for both environments
  if (isDevelopment) {
    return 'https://devnarfoundationfortheblind.org/admin';
  }
  
  return '/admin';
};

export const getReceiptUrl = (receiptId) => {
  if (!receiptId) return null;
  const encodedId = btoa(String(receiptId * 123456789));
  return `${getBaseUrl()}/receipt-share/${encodedId}`;
};

export const BASE_URL = getBaseUrl();

console.log('🔧 API Base URL:', BASE_URL);

// Helper function to build API URLs - ensures proper slash handling
const buildUrl = (path) => {
  const base = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
};

// ============================================
// API Service - Unified
// ============================================

const apiService = {
  saveData: async (table, data) => {
    try {
      const payload = {
        table: table,
        data: data
      };
      
      // ✅ Use buildUrl helper
      const response = await fetch(buildUrl('frontend/save'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API Error:', error);
      return {
        success: false,
        message: 'Network error: ' + error.message
      };
    }
  },

  // Check if donor exists by phone number
  checkDonorByPhone: async (phone) => {
    try {
      const response = await fetch(buildUrl(`frontend/checkDonorByPhone?phone=${phone}`), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      console.log('📞 Donor check result:', result);
      return result;
    } catch (error) {
      console.error('Error checking donor:', error);
      return { success: false, message: 'Failed to check donor', data: { exists: false } };
    }
  },

  // Create Razorpay order
  createRazorpayOrder: async (data) => {
    try {
      console.log('💰 Creating Razorpay order with data:', data);
      
      const formData = new URLSearchParams();
      formData.append('amount', data.amount);
      formData.append('donor_id', data.donor_id || '');
      formData.append('donation_type', data.donation_type || 'general');
      formData.append('first_name', data.first_name || '');
      formData.append('last_name', data.last_name || '');
      formData.append('email', data.email || '');
      formData.append('phone', data.phone || '');
      formData.append('pan_card', data.pan_card || '');
      formData.append('address', data.address || '');
      formData.append('purpose', data.purpose || '');
      formData.append('purpose_id', data.purpose_id || '');
      formData.append('meal_id', data.meal_id || '');
      formData.append('meal_date', data.meal_date || '');
      formData.append('visit_date', data.visit_date || '');
      formData.append('quantity', data.quantity || '');

      // ✅ Use buildUrl helper
      const response = await fetch(buildUrl('frontend/create_razorpay_order'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('💰 Order response:', result);
      return result;
    } catch (error) {
      console.error('Error creating order:', error);
      return { status: false, message: 'Failed to create order' };
    }
  },

  // Verify Razorpay payment
  verifyRazorpayPayment: async (data) => {
    try {
      console.log('🔐 Verifying payment...');
      
      const formData = new URLSearchParams();
      formData.append('razorpay_payment_id', data.razorpay_payment_id);
      formData.append('razorpay_order_id', data.razorpay_order_id);
      formData.append('razorpay_signature', data.razorpay_signature);

      // ✅ Use buildUrl helper
      const response = await fetch(buildUrl('frontend/verify_razorpay_payment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData
      });
      
      const result = await response.json();
      console.log('🔐 Verify response:', result);
      return result;
    } catch (error) {
      console.error('Error verifying payment:', error);
      return { status: false, message: 'Failed to verify payment' };
    }
  },

  // Save donation
  saveDonation: async (data) => {
    try {
      // ✅ Use buildUrl helper
      const url = buildUrl('frontend/api_save_donation');
      const formData = new URLSearchParams();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error saving donation:', error);
      return { success: false, message: 'Failed to save donation' };
    }
  },

  // Check payment status
  checkPaymentStatus: async (data) => {
    try {
      console.log('🔍 Checking payment status...');
      
      const formData = new URLSearchParams();
      formData.append('razorpay_payment_id', data.razorpay_payment_id);
      formData.append('razorpay_order_id', data.razorpay_order_id);

      // ✅ Use buildUrl helper
      const response = await fetch(buildUrl('frontend/check_payment_status'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData
      });
      
      const result = await response.json();
      console.log('🔍 Status response:', result);
      return result;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return { status: false, paid: false };
    }
  },

  // ============================================
  // Fetch functions - all use buildUrl
  // ============================================
  
  fetchAwards: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_awards'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching awards:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchSuccessStory: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=impact_data'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching success story:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchAboutSections: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_us'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching about sections:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchAboutStory: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_story'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching about story:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchAboutStats: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_stats'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching about stats:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchAboutDifferentiators: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_differentiators'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching about differentiators:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchAboutPartners: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_partners'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching about partners:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchCorrespondent: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_correspondent'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching correspondent:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchStaff: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_staff'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching staff:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchInternationalVolunteers: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_volunteers'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching international volunteers:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchGalleryMedia: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=gallery_media'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching gallery media:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchBanners: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=banners'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching banners:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchAboutFounder: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=about_founder'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching about founder:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchWebinfo: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=website_info'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching web info:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchVolunteers: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=volunteer_testimonials'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching volunteers:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchTestimonials: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=vip_testimonials'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching testimonials:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchCsrPartners: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=csr_partners'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching CSR partners:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchCsrTestimonials: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=csr_testimonials'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching CSR testimonials:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchPurposeMaster: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=purpose_master'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching purpose master:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchFoodMaster: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=food_master'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching food master:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },

  fetchFoodSponsorships: async () => {
    try {
      const response = await fetch(buildUrl('api/fetch?key=food_sponsorships'), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      console.log('🍽️ Food Sponsorships response:', result);
      return result;
    } catch (error) {
      console.error('Error fetching food sponsorships:', error);
      return { status: 'error', data: [], message: error.message };
    }
  },
  
  checkHoliday: async (date) => {
    try {
      const formData = new URLSearchParams();
      formData.append('date', date);

      const response = await fetch(`${BASE_URL}/frontend/check_holiday`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData
      });
      const result = await response.json();
      console.log('📅 Holiday check:', result);
      return result;
    } catch (error) {
      console.error('Error checking holiday:', error);
      // Fail open — allow the user to proceed if the check errors
      return { status: true, is_holiday: false };
    }
  },
};

export default apiService;